import React, { useState, useEffect } from "react";
import { Calendar, Info, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useFlowContext } from "../hooks/useFlowContext";
import { useCronograma } from "../hooks/useCronograma";
import { notifySuccess, notifyError } from "../utils/notify";
import {
  formatarData,
  calcularDiasRestantes,
  getStatusColor,
} from "../utils/dateUtils";
import { mandatoService } from "../services/mandatoService";
import { useMandatoAtivo } from "../hooks/useMandatoAtivo";

/**
 * Header Planejamento
 * Mostra banner de modo planejamento com plano do usuário
 */
const HeaderPlanejamento: React.FC<{ plano: string }> = ({ plano }) => (
  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-6 shadow-lg">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold mb-2">Planejamento de Cronograma</h1>
        <p className="text-blue-100">Simule as datas do processo eleitoral</p>
        <p className="text-sm text-blue-200 mt-1">
          Baseado na NR-05 - Portaria MTP nº 4.219/2022
        </p>
      </div>
      <div className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm">
        <span className="text-sm font-medium">Plano: {plano}</span>
      </div>
    </div>
  </div>
);

/**
 * Informações do Mandato Ativo
 * Mostra detalhes do mandato e permite recalcular
 */
interface MandatoInfoProps {
  mandato: any;
  onRecalcular: () => void;
  carregando: boolean;
}

const MandatoInfo: React.FC<MandatoInfoProps> = ({
  mandato,
  onRecalcular,
  carregando,
}) => (
  <div className="bg-white rounded-lg shadow-lg p-6">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900">
          Mandato #{mandato.numero_mandato} - Ativo
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Período: {formatarData(mandato.data_inicio)} até{" "}
          {formatarData(mandato.data_fim)}
        </p>
      </div>
      <button
        onClick={onRecalcular}
        disabled={carregando}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        {carregando ? "Recalculando..." : "Recalcular Cronograma"}
      </button>
    </div>
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-start gap-2">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">
            Mandato ativo encontrado
          </p>
          <p className="text-sm text-blue-700 mt-1">
            O cronograma abaixo foi calculado com base na data de término do seu
            mandato. Para alterar, clique em "Recalcular Cronograma".
          </p>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Date Picker Section
 * Seção para selecionar data de término e calcular cronograma
 */
interface DatePickerSectionProps {
  value: string;
  onChange: (value: string) => void;
  onCalcular: () => void;
  carregando?: boolean;
}

const DatePickerSection: React.FC<DatePickerSectionProps> = ({
  value,
  onChange,
  onCalcular,
  carregando = false,
}) => (
  <div className="bg-white rounded-lg shadow-lg p-6">
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Data de Término do Mandato *
        </label>
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          min={new Date().toISOString().split("T")[0]}
          disabled={carregando}
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">
              Duração do Mandato (NR-05)
            </p>
            <p className="text-sm text-blue-700 mt-1">
              A NR-05 permite mandatos de 1 a 2 anos. O sistema criará
              automaticamente um mandato e calculará as 9 fases do processo
              eleitoral.
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={onCalcular}
        disabled={!value || carregando}
        className={`w-full py-3 rounded-lg font-medium transition-all ${
          value && !carregando
            ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        <div className="flex items-center justify-center gap-2">
          <Calendar className="w-5 h-5" />
          {carregando ? "Criando mandato..." : "Criar Mandato e Calcular"}
        </div>
      </button>
    </div>
  </div>
);

/**
 * Upgrade Card
 * Card com CTA para upgrade de plano
 */
interface UpgradeCardProps {
  plano: string;
}

const UpgradeCard: React.FC<UpgradeCardProps> = ({ plano }) => (
  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-md">
    <div className="flex items-start gap-4">
      <div className="bg-blue-100 rounded-full p-3">
        <Zap className="w-6 h-6 text-blue-600" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          Modo Planejamento - Plano {plano}
        </h3>
        <p className="text-gray-700 mb-3">
          Você está visualizando um cronograma simulado. Esta funcionalidade
          permite apenas planejar as datas do processo eleitoral.
        </p>
        <div className="bg-white rounded-lg p-4 mb-4 border border-blue-100">
          <p className="text-sm font-medium text-gray-900 mb-2">
            🔒 Recursos disponíveis apenas no plano ENTERPRISE:
          </p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>
              • Gestão completa de mandatos (histórico, presidente, secretário)
            </li>
            <li>• Gestão de membros da CIPA</li>
            <li>• Cronograma de reuniões</li>
            <li>• Ata digital com assinatura</li>
            <li>• Plano de trabalho anual</li>
            <li>• Votação digital + apuração automática</li>
            <li>• Relatórios avançados e auditoria</li>
          </ul>
        </div>
        <button
          onClick={() => (window.location.href = "/planos")}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
        >
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Ver Planos e Fazer Upgrade
          </div>
        </button>
      </div>
    </div>
  </div>
);

/**
 * Visualização de Fases (read-only)
 */
interface FasesVisualizationProps {
  fases: any[]; // FaseComAcoes from useCronograma
}

const FasesVisualization: React.FC<FasesVisualizationProps> = ({ fases }) => (
  <div className="space-y-4">
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">
        Cronograma Salvo no Sistema
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Cronograma completo do seu mandato CIPA. As datas e ações estão salvas
        no banco de dados.
      </p>

      <div className="space-y-3">
        {fases.map((fase, index) => {
          const IconComponent = fase.icon;
          const diasRestantes = calcularDiasRestantes(fase.data);
          const statusColor = getStatusColor(fase.data);

          // Usar acoes_banco (do useCronograma) ou acoes (fallback)
          const acoesParaMostrar = fase.acoes_banco || fase.acoes || [];

          return (
            <div
              key={fase.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50"
            >
              <div className="flex items-start gap-3">
                <div className={`${fase.cor} rounded-full p-2 text-white`}>
                  <IconComponent className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Fase {index + 1}: {fase.titulo}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {fase.prazoNR} • {fase.artigo}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {formatarData(fase.data)}
                      </p>
                      <p className={`text-xs ${statusColor} mt-1`}>
                        {diasRestantes > 0
                          ? `${diasRestantes} dias`
                          : diasRestantes === 0
                            ? "Hoje"
                            : `${Math.abs(diasRestantes)} dias atrás`}
                      </p>
                    </div>
                  </div>

                  {acoesParaMostrar.length > 0 && (
                    <div className="mt-3 bg-white rounded-md p-3 border border-gray-100">
                      <p className="text-xs font-medium text-gray-700 mb-2">
                        Ações necessárias ({acoesParaMostrar.length}):
                      </p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {acoesParaMostrar.map((acao: any, idx: number) => (
                          <li
                            key={acao.id || idx}
                            className="flex items-start gap-2"
                          >
                            <span className="text-blue-500 mt-0.5">•</span>
                            <span>{acao.descricao || acao}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/**
 * Componente Principal: Cronograma Planejamento Tab
 * Para usuários STARTER/PROFESSIONAL sem mandato ativo
 */
export const CronogramaPlanejamentoTab: React.FC = () => {
  // Estados
  const [dataFimMandato, setDataFimMandato] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [mandatoCriado, setMandatoCriado] = useState(false);

  // Contextos e Hooks
  const { empresaSelecionada, user } = useAuth();
  const { context } = useFlowContext();
  const { mandatoAtivo, recarregar: refreshMandato } = useMandatoAtivo();
  const { fases, loading: loadingFases, recriarCronograma } = useCronograma();

  const planoUsuario = context.planoUsuario;
  const tipoComissao = (empresaSelecionada as any)?.tipo_comissao || "CIPA";

  console.log("🔍 [CronogramaPlanejamentoTab] Contexto:", {
    planoUsuario,
    tipoComissao,
    empresaSelecionada: (empresaSelecionada as any)?.nome,
    mandatoAtivo: mandatoAtivo?.id,
    totalFases: fases.length,
  });

  // Verificar se já existe mandato ativo ao carregar
  useEffect(() => {
    if (mandatoAtivo) {
      setMandatoCriado(true);
      setDataFimMandato(mandatoAtivo.data_fim);
    }
  }, [mandatoAtivo]);

  // Função principal: calcular cronograma e criar mandato
  const calcularCronograma = async () => {
    if (!dataFimMandato) {
      notifyError("Selecione a data de término do mandato");
      return;
    }

    if (!empresaSelecionada?.id || !user?.id) {
      notifyError("Dados da empresa ou usuário não encontrados");
      return;
    }

    setCarregando(true);

    try {
      // 1. Verificar se já existe mandato ativo
      if (mandatoAtivo) {
        console.log("⚠️ Já existe mandato ativo, recriando cronograma...");
        // Se já existe, apenas recriar as fases
        await recriarCronograma(dataFimMandato);
        notifySuccess("Cronograma recalculado com sucesso!");
        setMandatoCriado(true);
        return;
      }

      // 2. Calcular data de início (1 ano antes do término)
      const dataFim = new Date(dataFimMandato);
      const dataInicio = new Date(dataFim);
      dataInicio.setFullYear(dataInicio.getFullYear() - 1);

      console.log("📅 Criando mandato:", {
        dataInicio: dataInicio.toISOString().split("T")[0],
        dataFim: dataFimMandato,
      });

      // 3. Buscar último número de mandato
      const ultimoMandato = await mandatoService.listar(empresaSelecionada.id);
      const numeroMandato =
        ultimoMandato.length > 0
          ? Math.max(...ultimoMandato.map((m) => m.numero_mandato)) + 1
          : 1;

      // 4. Criar mandato no banco
      const novoMandato = await mandatoService.criar({
        empresa_id: empresaSelecionada.id,
        numero_mandato: numeroMandato,
        data_inicio: dataInicio.toISOString().split("T")[0],
        data_fim: dataFimMandato,
        status: "ativo",
        eleicao_realizada: false,
        criado_por: user.id,
      });

      console.log("✅ Mandato criado:", novoMandato);

      // 5. Atualizar mandato ativo
      await refreshMandato();

      // 6. Criar fases do cronograma usando hook
      await recriarCronograma(dataFimMandato);

      setMandatoCriado(true);
      notifySuccess(`Mandato #${numeroMandato} criado e cronograma calculado!`);
    } catch (error: any) {
      console.error("❌ Erro ao criar mandato:", error);
      notifyError(error.message || "Erro ao criar mandato");
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header com aviso de modo planejamento */}
      <HeaderPlanejamento plano={planoUsuario} />

      {/* Informações do Mandato (se já existe) */}
      {mandatoCriado && mandatoAtivo && (
        <MandatoInfo
          mandato={mandatoAtivo}
          onRecalcular={() => {
            setMandatoCriado(false);
            setDataFimMandato(mandatoAtivo.data_fim);
          }}
          carregando={carregando}
        />
      )}

      {/* Date Picker - Só mostrar se não tem mandato criado */}
      {!mandatoCriado && (
        <DatePickerSection
          value={dataFimMandato}
          onChange={setDataFimMandato}
          onCalcular={calcularCronograma}
          carregando={carregando}
        />
      )}

      {/* Visualização de Fases (quando calculado e carregado) */}
      {mandatoCriado && !loadingFases && fases.length > 0 && (
        <>
          <FasesVisualization fases={fases} />
          <UpgradeCard plano={planoUsuario} />
        </>
      )}

      {/* Loading state */}
      {(carregando || loadingFases) && (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">
            {carregando ? "Criando mandato..." : "Carregando cronograma..."}
          </p>
        </div>
      )}
    </div>
  );
};

export default CronogramaPlanejamentoTab;
